import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Button, LuneTheme, Text, ToggleTabs, useIsMobile } from '@lune-fe/lune-ui-lib'
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined'
import CloudDownloadIcon from '@mui/icons-material/CloudDownloadOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import { Box, Stack } from '@mui/material'
import fileDownload from 'js-file-download'
import DownloadDropdown from 'lune-ui-lib/src/components/DownloadDropdown'
import HorizontalSegmentedBar, { Segment } from 'lune-ui-lib/src/components/HorizontalSegmentedBar'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Range } from 'react-date-range'

import { formatNumbers } from '../../utils'
import {
    downloadImageFromHtml,
    emissionsChartColors,
    emissionsChartLabels,
    IEmissionsTimeseries,
    IWtwBreakdownTimeseries,
    logisticsChartOrdering,
    LogisticsEmissionsCategories,
    wtwBreakdownChartColors,
    wtwBreakdownChartLabels,
    wtwBreakdownChartOrdering,
    WtwStage,
} from '../charts/chartUtils'
import EmissionsChartSection from '../charts/EmissionsChartSection'

const { palette } = LuneTheme
const baseCategoryBarData = {
    air: {
        value: 0,
        label: 'Air',
        unit: 't',
        order: logisticsChartOrdering.air,
        colour: palette.Data300,
    },
    sea: {
        value: 0,
        label: 'Sea',
        unit: 't',
        order: logisticsChartOrdering.sea,
        colour: palette.Data100,
    },
    road: {
        value: 0,
        label: 'Road',
        order: logisticsChartOrdering.road,
        unit: 't',
        colour: palette.Data500,
    },
    rail: {
        value: 0,
        label: 'Rail',
        order: logisticsChartOrdering.rail,
        unit: 't',
        colour: palette.Data700,
    },
    'inland waterway': {
        value: 0,
        label: 'Inland waterway',
        unit: 't',
        order: logisticsChartOrdering.inlandWaterway,
        colour: palette.Data800,
    },
    'logistics sites': {
        value: 0,
        label: 'Logistics sites',
        unit: 't',
        order: logisticsChartOrdering.logisticSite,
        colour: palette.Data900,
    },
}

const baseWtwBarData = {
    'Tank-To-Wheel': {
        value: 0,
        label: 'Tank-To-Wheel',
        unit: 't',
        order: wtwBreakdownChartOrdering.ttw,
        colour: palette.Data300,
    },
    'Well-To-Tank': {
        value: 0,
        label: 'Well-To-Tank',
        unit: 't',
        order: wtwBreakdownChartOrdering.wtt,
        colour: palette.Data100,
    },
    'unknown stage': {
        value: 0,
        label: 'Unknown stage',
        unit: 't',
        order: wtwBreakdownChartOrdering.unknown,
        colour: palette.Data1400,
    },
}

export const EmissionsSectionChartToggle: FC<{
    data: {
        emissionTimeseries: IEmissionsTimeseries[]
        wtwBreakdownTimeseries: IWtwBreakdownTimeseries[]
    }
    dateRange: Range
    loading: boolean
    emissionsListProps?: {
        organisationId: string
        type: string
        accountHandle: string
        accessToken?: string
    }
    accountId?: string
    csvDownloadFn?: (
        organisationId: string,
        type: string,
        accountHandle: string,
        from: string,
        through: string,
        accessToken?: string,
    ) => Promise<string>
    // eslint-disable-next-line complexity
}> = ({
    data: { emissionTimeseries, wtwBreakdownTimeseries },
    dateRange,
    loading,
    emissionsListProps,
    accountId,
    csvDownloadFn,
}) => {
    useLingui()

    const printRef = useRef<HTMLElement>(null)
    const { isMobile } = useIsMobile()
    const [total, setTotal] = useState<string>()
    const [categoryBarData, setCategoryBarData] = useState<Segment[]>([
        baseCategoryBarData.air,
        baseCategoryBarData.sea,
        baseCategoryBarData.road,
        baseCategoryBarData.rail,
        baseCategoryBarData['inland waterway'],
        baseCategoryBarData['logistics sites'],
    ])
    const [wtwBarData, setWtwBarData] = useState<Segment[]>([
        baseWtwBarData['unknown stage'],
        baseWtwBarData['Tank-To-Wheel'],
        baseWtwBarData['Well-To-Tank'],
    ])

    const downloadCsv = () => {
        if (csvDownloadFn && emissionsListProps) {
            csvDownloadFn(
                emissionsListProps.organisationId,
                emissionsListProps.type,
                emissionsListProps.accountHandle,
                dateRange.startDate!.toLocaleDateString('sv-SE'),
                dateRange.endDate!.toLocaleDateString('sv-SE'),
                emissionsListProps.accessToken,
            ).then((data) =>
                fileDownload(data, `logistics-emissions-${emissionsListProps.accountHandle}.csv`),
            )
        }
    }

    const downloadChart = () => {
        if (printRef.current) {
            downloadImageFromHtml(printRef.current, `emissions-graph`)
        }
    }

    const emissionsListPath: string | undefined = useMemo(() => {
        if (!emissionsListProps) {
            return undefined
        }
        const { organisationId, type, accountHandle } = emissionsListProps
        const queryParams = window.location.href.split('?')[1]
        const basePath =
            type === 'live'
                ? `/logistics/${organisationId}/${accountHandle}/emissions`
                : `/logistics/test/${organisationId}/${accountHandle}/emissions`
        return `${basePath}${queryParams ? `?${queryParams}` : ''}`
    }, [
        emissionsListProps?.organisationId,
        emissionsListProps?.type,
        emissionsListProps?.accountHandle,
        window.location.href,
    ])

    const showOffsetButton = useMemo(() => {
        if (typeof window === 'undefined') {
            return false
        }
        const params = new URLSearchParams(window.location.search)
        return params.get('show_offset') === 'true'
    }, [window.location.href])

    useEffect(() => {
        let airTotal = 0
        let seaTotal = 0
        let roadTotal = 0
        let railTotal = 0
        let inlandWaterwayTotal = 0
        let logisticsSiteTotal = 0
        let wttTotal = 0
        let ttwTotal = 0
        let wtwUnknownTotal = 0

        for (const item of emissionTimeseries) {
            airTotal += item.air
            seaTotal += item.sea
            roadTotal += item.road
            railTotal += item.rail
            inlandWaterwayTotal += item.inlandWaterway
            logisticsSiteTotal += item.logisticSite
        }
        for (const item of wtwBreakdownTimeseries) {
            wttTotal += item.wtt
            ttwTotal += item.ttw
            wtwUnknownTotal += item.unknown
        }

        const barCategoryData: Segment[] = [
            {
                ...baseCategoryBarData.air,
                value: airTotal,
            },
            {
                ...baseCategoryBarData.sea,
                value: seaTotal,
            },
            {
                ...baseCategoryBarData.road,
                value: roadTotal,
            },
            {
                ...baseCategoryBarData.rail,
                value: railTotal,
            },
            {
                ...baseCategoryBarData['inland waterway'],
                value: inlandWaterwayTotal,
            },
            {
                ...baseCategoryBarData['logistics sites'],
                value: logisticsSiteTotal,
            },
        ].filter((s) => s.value > 0)
        const barWtwData: Segment[] = [
            {
                ...baseWtwBarData['Well-To-Tank'],
                value: wttTotal,
            },
            {
                ...baseWtwBarData['unknown stage'],
                value: wtwUnknownTotal,
            },
            {
                ...baseWtwBarData['Tank-To-Wheel'],
                value: ttwTotal,
            },
        ].filter((s) => s.label !== baseWtwBarData['unknown stage'].label || s.value > 0)
        setTotal(
            formatNumbers(
                airTotal +
                    seaTotal +
                    roadTotal +
                    railTotal +
                    inlandWaterwayTotal +
                    logisticsSiteTotal,
                2,
            ).toString(),
        )
        setCategoryBarData(barCategoryData)
        setWtwBarData(barWtwData)
    }, [emissionTimeseries, wtwBreakdownTimeseries])

    const downloadButton = csvDownloadFn ? (
        <DownloadDropdown
            downloadPropMap={{
                csv: {
                    downloadFn: downloadCsv,
                    title: t`CSV`,
                    icon: <TableChartOutlinedIcon />,
                },
                chart: {
                    downloadFn: downloadChart,
                    title: t`Image`,
                    icon: <BarChartOutlinedIcon />,
                },
            }}
        />
    ) : (
        <Button
            onClick={() => {
                if (printRef.current) {
                    downloadImageFromHtml(printRef.current, `shipping-emissions-graph`)
                }
            }}
            leftIcon={<FileDownloadOutlinedIcon />}
            variant={'outlined'}
        >
            {t`Download graph`}
        </Button>
    )
    const byCategoryChart = (
        <>
            {!!categoryBarData.length && (
                <HorizontalSegmentedBar
                    segments={categoryBarData.map((segment) => ({
                        ...segment,
                        label:
                            segment.label === 'Air'
                                ? t`Air`
                                : segment.label === 'Sea'
                                  ? t`Sea`
                                  : segment.label === 'Road'
                                    ? t`Road`
                                    : segment.label === 'Rail'
                                      ? t`Rail`
                                      : segment.label === 'Inland waterway'
                                        ? t`Inland waterway`
                                        : segment.label === 'Logistics sites'
                                          ? t`Logistics sites`
                                          : segment.label,
                    }))}
                    legendOptions={{ ontop: true, vertical: false }}
                />
            )}
            <EmissionsChartSection
                loading={loading}
                data={emissionTimeseries}
                dataColours={emissionsChartColors}
                dataLabels={emissionsChartLabels}
                dateRange={dateRange}
                showLegend={false}
                skipZeroOnTooltip={false}
                labelOrdering={(a: LogisticsEmissionsCategories, b: LogisticsEmissionsCategories) =>
                    logisticsChartOrdering[a] - logisticsChartOrdering[b]
                }
            />
        </>
    )
    const byWtwChart = (
        <>
            {!!wtwBarData.length && (
                <HorizontalSegmentedBar
                    segments={wtwBarData.map((segment) => ({
                        ...segment,
                        label:
                            segment.label === 'Tank-To-Wheel'
                                ? t`Tank-To-Wheel`
                                : segment.label === 'Well-To-Tank'
                                  ? t`Well-To-Tank`
                                  : segment.label === 'Unknown stage'
                                    ? t`Unknown stage`
                                    : segment.label,
                    }))}
                    legendOptions={{ ontop: true, vertical: false }}
                />
            )}
            <EmissionsChartSection
                loading={loading}
                data={wtwBreakdownTimeseries}
                dataColours={wtwBreakdownChartColors}
                dataLabels={wtwBreakdownChartLabels}
                dateRange={dateRange}
                showLegend={false}
                skipZeroOnTooltip={false}
                labelOrdering={(a: WtwStage, b: WtwStage) =>
                    wtwBreakdownChartOrdering[a] - wtwBreakdownChartOrdering[b]
                }
            />
        </>
    )

    return (
        <>
            <Box sx={{ mt: isMobile ? 5 : 9 }}>
                <Box ref={printRef} sx={{ mt: '0 !important' }}>
                    <Text variant={'h4'} sx={{ mb: isMobile ? 5 : 9 }}>
                        {t`Your shipments have emitted ${total} tCO₂e`}
                    </Text>
                    {
                        // For estimates before this date there will not be WTW breakdown data since
                        // emission factors were not introduced. Do not show WTW breakdown graph
                        // Note: We are aware that shipped_at !== created_at, meaning that we might
                        // be hiding some valid WTW breakdown data with this filter, even so we have
                        // chosen to press onward with this change since showing empty data looks bad.
                        dateRange.startDate && dateRange.startDate < new Date('2024-11-08') ? (
                            byCategoryChart
                        ) : (
                            <ToggleTabs defaultActive={location.hash.slice(1)}>
                                <ToggleTabs.Tab label={t`By category`} value={'category'}>
                                    {byCategoryChart}
                                </ToggleTabs.Tab>
                                <ToggleTabs.Tab label={t`By WTW stage`} value={'wtwStage'}>
                                    {byWtwChart}
                                </ToggleTabs.Tab>
                            </ToggleTabs>
                        )
                    }
                </Box>
            </Box>
            <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={1}>
                {showOffsetButton && accountId && (
                    <Button
                        leftIcon={<CloudDownloadIcon />}
                        variant={'contained'}
                        href={`https://pay.lune.co/${accountId}?quantity=${total}`}
                        sx={isMobile ? { width: '100%' } : {}}
                    >
                        {t`Offset now`}
                    </Button>
                )}
                {emissionsListPath && (
                    <Button
                        leftIcon={<ListAltOutlinedIcon />}
                        variant={'outlined'}
                        href={emissionsListPath}
                    >
                        {t`View individual calculations`}
                    </Button>
                )}
                {!isMobile && dateRange.startDate && dateRange.endDate && downloadButton}
            </Stack>
        </>
    )
}

export default EmissionsSectionChartToggle
